import { useId } from 'react';
import { Link } from 'react-router';
import { formatPeso } from '@tahi/core';
import { cn } from './cn';
import { Icon, type IconName } from './icons';

const FOCUS =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[-3px] focus-visible:outline-[var(--ring)]';

export interface MenuItem {
  to: string;
  label: string;
  icon: IconName;
  /** A short live value on the right ("2 sources", "Off"). Omit it rather than show a guess. */
  value?: string | null;
  /** An amount on the right, in DM Mono; negative shows as −₱ in red. Wins over `value`. */
  amountMinor?: number | null;
  /** Words after a non-negative amount ("left"); a negative amount stands alone, in red. */
  amountSuffix?: string;
}

/** "₱1,234" or "−₱1,234" (a real minus sign), whole pesos when there are no centavos. */
function signed(minor: number): string {
  const text = formatPeso(Math.abs(minor), { cents: 'auto' });
  return minor < 0 ? `−${text}` : text;
}

function MenuRow({ item }: { item: MenuItem }) {
  const valueId = useId();
  const amount = item.amountMinor ?? null;
  const hasValue = amount !== null || Boolean(item.value);
  return (
    <li>
      <Link
        to={item.to}
        // The name stays the row's label; the value is read as its description.
        aria-label={item.label}
        aria-describedby={hasValue ? valueId : undefined}
        className={cn(
          'flex min-h-12 items-center gap-3 px-4 py-2 text-fg hover:bg-secondary',
          FOCUS,
        )}
      >
        <Icon name={item.icon} className="shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {amount !== null ? (
          <span
            id={valueId}
            className={cn(
              'shrink-0 font-mono text-sm tabular-nums',
              amount < 0 ? 'text-destructive' : 'text-muted',
            )}
          >
            {signed(amount)}
            {item.amountSuffix && amount >= 0 ? (
              <span className="font-sans"> {item.amountSuffix}</span>
            ) : null}
          </span>
        ) : item.value ? (
          <span id={valueId} className="shrink-0 text-sm text-muted">
            {item.value}
          </span>
        ) : null}
        <Icon name="chevron" size={18} className="-mr-1 shrink-0 text-muted" />
      </Link>
    </li>
  );
}

/**
 * v0.7.5 (TAHI-23): a module's menu, one card of tappable rows (icon, name, a short live value,
 * ›). Every module's main screen ends with one instead of a row of small text links.
 */
export function MenuCard({
  label,
  items,
  className,
}: {
  /** The nav's accessible name. */
  label: string;
  items: ReadonlyArray<MenuItem | false | null | undefined>;
  className?: string;
}) {
  const rows = items.filter((i): i is MenuItem => Boolean(i));
  if (rows.length === 0) return null;
  return (
    <nav
      aria-label={label}
      className={cn('overflow-hidden rounded-3xl border border-divider bg-surface-2', className)}
    >
      <ul className="flex flex-col divide-y divide-divider">
        {rows.map((item) => (
          <MenuRow key={item.to} item={item} />
        ))}
      </ul>
    </nav>
  );
}
